import React, { memo, useMemo } from 'react';
import { Card, Statistic, Tooltip } from 'antd';
import { RiseOutlined, FallOutlined, MinusOutlined } from '@ant-design/icons';

interface KPICardProps {
  title: string;
  value: number | string;
  previousValue?: number | string;
  prefix?: React.ReactNode;
  suffix?: string;
  isCurrency?: boolean;
  valueStyle?: React.CSSProperties;
  loading?: boolean;
  sparklineData?: number[]; // Optional timeseries data for sparkline
  /** When 'admin', renders design-system stat card (icon box, growth badge, large value) */
  variant?: 'default' | 'admin';
}

/** Coerce API value to number; treat NaN/undefined/null as 0 */
function ensureNumber(x: number | string | undefined | null): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format number with abbreviation (e.g., 1.2M, 500K).
 * Uses Intl.NumberFormat for currency. Returns both abbreviated and full value.
 */
/** VND format with dot as thousand separator (e.g. 1.345.000 đ) */
const formatVnd = (value: number): string => {
  const safe = ensureNumber(value);
  return (
    new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(safe) + ' đ'
  );
};

const formatNumber = (
  value: number,
  isCurrency = false
): { display: string; full: string } => {
  const safe = ensureNumber(value);
  const absValue = Math.abs(safe);
  const locale = isCurrency ? 'vi-VN' : 'en-US';
  const intlNum = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: isCurrency ? 2 : 0,
  });
  let display: string;
  let full: string;

  if (isCurrency) {
    full = formatVnd(safe);
    if (absValue >= 1000000000) {
      display = (safe / 1000000000).toFixed(1).replace('.', ',') + 'B đ';
    } else if (absValue >= 1000000) {
      display = (safe / 1000000).toFixed(1).replace('.', ',') + 'M đ';
    } else if (absValue >= 1000) {
      display = (safe / 1000).toFixed(1).replace('.', ',') + 'k đ';
    } else {
      display = full;
    }
  } else {
    full = intlNum.format(safe);
    if (absValue >= 1000000) {
      display = (safe / 1000000).toFixed(1) + 'M';
    } else if (absValue >= 1000) {
      display = (safe / 1000).toFixed(1) + 'k';
    } else {
      display = full;
    }
  }

  return { display, full };
};

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  previousValue,
  prefix,
  suffix,
  isCurrency = false,
  valueStyle,
  loading = false,
  sparklineData,
  variant = 'default',
}) => {
  const numValue = useMemo(() => ensureNumber(value), [value]);
  const numPrev = useMemo(() => ensureNumber(previousValue), [previousValue]);

  const formattedValue = useMemo(
    () => formatNumber(numValue, isCurrency),
    [numValue, isCurrency]
  );

  const calculateTrend = (): {
    percentage: number | null;
    isPositive: boolean;
    tooltip: string;
    hasData: boolean;
  } => {
    if (numPrev === 0 || !Number.isFinite(numPrev)) {
      return {
        percentage: null,
        isPositive: true,
        tooltip: 'Không có dữ liệu so sánh',
        hasData: false,
      };
    }

    const diff = numValue - numPrev;
    const percentage = (diff / numPrev) * 100;
    const safePct = Number.isFinite(percentage) ? percentage : 0;
    const isPositive = diff >= 0;

    const prevFormatted = formatNumber(numPrev, isCurrency);
    const tooltip = isPositive
      ? `Tăng ${Math.abs(safePct).toFixed(1)}% so với kỳ trước\nKỳ trước: ${
          prevFormatted.full
        }${suffix || ''}`
      : `Giảm ${Math.abs(safePct).toFixed(1)}% so với kỳ trước\nKỳ trước: ${
          prevFormatted.full
        }${suffix || ''}`;

    return {
      percentage: Math.abs(safePct),
      isPositive,
      tooltip,
      hasData: true,
    };
  };

  const trend = calculateTrend();

  if (variant === 'admin') {
    const rawGrowth =
      trend.hasData && trend.percentage !== null ? trend.percentage : null;
    const growthPercent =
      rawGrowth !== null && Number.isFinite(rawGrowth)
        ? Math.round(rawGrowth)
        : null;
    return (
      <Card
        loading={loading}
        className="admin-stat-card"
        style={{ height: '100%' }}
      >
        <div className="admin-stat-card-header">
          <div className="admin-stat-card-icon">{prefix}</div>
          {growthPercent != null && (
            <span className="admin-stat-card-growth">
              <RiseOutlined style={{ fontSize: 10 }} />
              {growthPercent}%
            </span>
          )}
        </div>
        <div className="admin-stat-card-title">{title}</div>
        <div className="admin-stat-card-value">{formattedValue.display}</div>
      </Card>
    );
  }

  // Simple SVG sparkline (optional, only if data available)
  const renderSparkline = () => {
    if (!sparklineData || sparklineData.length < 2) return null;

    const nums = sparklineData.map(v => ensureNumber(v));
    const width = 80;
    const height = 20;
    const padding = 2;
    const max = Math.max(...nums);
    const min = Math.min(...nums);
    const range = max - min || 1;

    const points = nums
      .map((val, idx) => {
        const x = padding + (idx / (nums.length - 1)) * (width - padding * 2);
        const y =
          height - padding - ((val - min) / range) * (height - padding * 2);
        return `${x},${y}`;
      })
      .join(' ');

    const color = valueStyle?.color || '#1890ff';

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polygon
          points={`${padding},${height - padding} ${points} ${
            width - padding
          },${height - padding}`}
          fill={color}
          fillOpacity="0.1"
        />
      </svg>
    );
  };

  return (
    <Card loading={loading} style={{ height: '100%' }}>
      <Statistic
        title={title}
        value={formattedValue.display}
        prefix={prefix}
        suffix={suffix}
        valueStyle={valueStyle}
      />

      {/* Trend indicator */}
      <div style={{ marginTop: 8, minHeight: 24 }}>
        {trend.hasData ? (
          <Tooltip title={trend.tooltip}>
            <span
              style={{
                color: trend.isPositive ? '#52c41a' : '#ff4d4f',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontWeight: 500,
              }}
            >
              {trend.isPositive ? <RiseOutlined /> : <FallOutlined />}
              {trend.percentage !== null &&
                Number.isFinite(trend.percentage) &&
                `${trend.percentage.toFixed(1)}%`}
            </span>
          </Tooltip>
        ) : (
          <span
            style={{
              color: '#999',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <MinusOutlined />
            <span>—</span>
          </span>
        )}
      </div>

      {/* Sparkline (optional) */}
      {sparklineData && sparklineData.length > 0 && (
        <div
          style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}
        >
          {renderSparkline()}
        </div>
      )}

      {/* Tooltip on value to show full number */}
      {formattedValue.display !== formattedValue.full && (
        <Tooltip
          title={`Giá trị đầy đủ: ${formattedValue.full}${suffix || ''}`}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              cursor: 'help',
            }}
          />
        </Tooltip>
      )}
    </Card>
  );
};

export default memo(KPICard);
